// Diagnóstico: qualifica para a Sessão Clareza (consulta gratuita de 30 minutos com a Clara).
// Estrutura (funil): capa → blocos de perguntas com micro-conteúdos → análise → espelho → captura → resultado.
// As perguntas cobrem o formulário de qualificação da Sessão Clareza (objetivo, o que impede, acompanhamento
// anterior, urgência, investimento, renda, quem decide, como conheceu) e a realidade de quem mora na Austrália.
//
// Perfil: sai de pesos em várias respostas (PESOS), não de uma pergunta só. São 5 perfis pelo que trava a
// pessoa (Adaptação, Rotina Corrida, Fome Emocional, Recomeço, Saúde em Foco); a rotina vira contexto.
//
// Convenções de texto:
// - `**trecho**` vira destaque colorido na tela (componente Realce).
// - `{token}` é trocado pela resposta da pessoa (ver TOKENS e preencher()); vale para títulos, opções e micro-conteúdos.
// - Toda pergunta precisa ter uma opção em que qualquer pessoa caiba.
// - Texto de perfil é geral: só afirma o que vale para qualquer pessoa daquele perfil, com "costuma" em vez de
//   certeza. O específico só aparece quando vem de uma resposta dela (retrato, cards, itens da sessão).

export type PerfilId = "adaptacao" | "rotina" | "emocional" | "recomeco" | "saude";

/** Chaves das imagens em src/assets/quiz (o mapeamento para o arquivo fica na página). */
export type ImagemQuiz =
  | "tile-mulher"
  | "tile-homem"
  | "clara"
  | "comunidade"
  | "sistema"
  | "acompanhamento"
  | "sessao"
  | `perfil-${PerfilId}`
  | `perfil-${PerfilId}-homem`;

export type Respostas = Record<string, string | string[]>;

export interface Opcao {
  value: string;
  label: string;
  emoji?: string;
  /** Foto do tile (layout "fotos"). */
  imagem?: ImagemQuiz;
  /** Em múltipla escolha, marcar esta limpa as outras (ex.: "nada disso"). */
  exclusivo?: boolean;
  /** Só aparece para quem respondeu "mulher". */
  soMulher?: boolean;
}

interface TelaBase {
  id: string;
  /** Tela condicional: só aparece quando devolve true (ex.: "como foi" só para quem já fez acompanhamento). */
  mostrarSe?: (r: Respostas) => boolean;
}

export interface Pergunta extends TelaBase {
  kind: "pergunta";
  titulo: string;
  subtitulo?: string;
  /** Rótulo curto da pergunta no resumo que chega para a Clara. */
  curto: string;
  opcoes: Opcao[];
  multi?: boolean;
  /** lista (padrão): cards cheios com ícone; grade: 2 colunas só texto; fotos: tiles com foto; escala: 0 a 10. */
  layout?: "lista" | "grade" | "fotos" | "escala";
  /** Legendas das pontas da escala. */
  extremos?: [string, string];
}

export interface MicroTexto {
  titulo: string;
  corpo: string;
  destaque?: string;
}

export interface Micro extends TelaBase, MicroTexto {
  kind: "micro";
  /** Linha pequena acima do título. */
  eyebrow: string;
  imagem?: ImagemQuiz;
  /** Mostra a foto da Clara ao lado do destaque (fala dela). */
  assinado?: boolean;
  cta?: string;
  /** Texto escolhido pela resposta a uma pergunta anterior. */
  dinamico?: {
    /** Pergunta cuja resposta escolhe o texto (respeita telas escondidas). */
    perguntaId: string;
    /** Texto usado quando a pergunta ficou escondida ou sem resposta. */
    padrao?: string;
    textos: Record<string, MicroTexto>;
  };
}

export type Tela = Pergunta | Micro;

const ESCALA_0_10: Opcao[] = Array.from({ length: 11 }, (_, n) => ({ value: String(n), label: String(n) }));

export const TELAS: Tela[] = [
  // Bloco A: quem é você
  {
    kind: "pergunta",
    id: "sexo",
    curto: "Sexo",
    titulo: "Você é mulher ou homem?",
    layout: "fotos",
    opcoes: [
      { value: "mulher", label: "Mulher", imagem: "tile-mulher" },
      { value: "homem", label: "Homem", imagem: "tile-homem" },
    ],
  },
  {
    kind: "pergunta",
    id: "idade",
    curto: "Idade",
    titulo: "Qual é a sua idade?",
    layout: "grade",
    opcoes: [
      { value: "18-24", label: "18 a 24" },
      { value: "25-34", label: "25 a 34" },
      { value: "35-44", label: "35 a 44" },
      { value: "45+", label: "45 ou mais" },
    ],
  },
  {
    kind: "pergunta",
    id: "cidade",
    curto: "Cidade",
    titulo: "Onde você mora?",
    layout: "grade",
    opcoes: [
      { value: "Brisbane", label: "Brisbane" },
      { value: "Gold Coast", label: "Gold Coast" },
      { value: "Sydney", label: "Sydney" },
      { value: "Melbourne", label: "Melbourne" },
      { value: "Perth", label: "Perth" },
      { value: "Adelaide", label: "Adelaide" },
      { value: "Outra", label: "Outra cidade" },
    ],
  },
  {
    kind: "pergunta",
    id: "tempo_au",
    curto: "Tempo na Austrália",
    titulo: "Há quanto tempo você está na Austrália?",
    opcoes: [
      { value: "<6m", label: "Menos de 6 meses", emoji: "🛬" },
      { value: "6m-2a", label: "De 6 meses a 2 anos", emoji: "📅" },
      { value: "2-5a", label: "De 2 a 5 anos", emoji: "🏡" },
      { value: "5a+", label: "Mais de 5 anos", emoji: "🦘" },
    ],
  },
  {
    kind: "micro",
    id: "micro-1",
    eyebrow: "Você não está {sozinha}",
    imagem: "comunidade",
    titulo: "",
    corpo: "",
    destaque: "Eu também passei por isso quando cheguei. Hoje eu ajudo {brasileiras} nessa fase, no consultório e online.",
    assinado: true,
    cta: "Continuar",
    dinamico: {
      perguntaId: "tempo_au",
      textos: {
        "<6m": {
          titulo: "**Menos de 6 meses de Austrália:** a fase em que a comida vira o que é fácil.",
          corpo:
            "Pão, coisa pronta, delivery e a sensação de que tudo é caro. {cidade_frase} tem milhares de {brasileiras} que passaram por isso. {idade_frase} Vamos ver o que a sua rotina está fazendo com a sua comida.",
        },
        "6m-2a": {
          titulo: "**Entre 6 meses e 2 anos:** a rotina assentou, mas a alimentação ainda não.",
          corpo:
            "Você já sabe onde fica tudo no mercado, mas compra no automático e come no improviso. {cidade_frase} tem milhares de {brasileiras} nessa mesma fase. {idade_frase} Vamos ver o que mudou para você.",
        },
        "2-5a": {
          titulo: "**De 2 a 5 anos aqui:** o problema deixou de ser o mercado e virou a rotina.",
          corpo:
            "Você conhece o mercado de olhos fechados. O que pesa agora é o dia: trabalho, cansaço e decidir o que comer todo santo dia. {idade_frase} Vamos ver onde a sua semana está escapando.",
        },
        "5a+": {
          titulo: "**Mais de 5 anos de Austrália:** você já é de casa, e mesmo assim a comida saiu do lugar.",
          corpo:
            "A rotina encheu e a comida de verdade foi ficando para depois. {cidade_frase} tem milhares de {brasileiras} na mesma situação. {idade_frase} Vamos ver o que a sua semana precisa.",
        },
      },
    },
  },

  // Bloco B: rotina e o que mudou
  {
    kind: "pergunta",
    id: "rotina",
    curto: "Rotina",
    titulo: "Como é a sua rotina hoje?",
    opcoes: [
      { value: "turnos", label: "Turnos, hospitality ou limpeza", emoji: "☕" },
      { value: "estudo", label: "Estudo e trabalho ao mesmo tempo", emoji: "📚" },
      { value: "casa", label: "Cuido da casa e dos filhos", emoji: "👶" },
      { value: "escritorio", label: "Escritório, 9 às 5", emoji: "💻" },
      { value: "chegou", label: "Cheguei agora, ainda me organizando", emoji: "🧳" },
      { value: "outro", label: "Outra rotina", emoji: "✨" },
    ],
  },
  {
    kind: "pergunta",
    id: "atividade",
    curto: "Atividade física",
    titulo: "Quanto você se movimenta na semana?",
    opcoes: [
      { value: "pouco", label: "Quase nada", emoji: "🛋️" },
      { value: "em_pe", label: "Trabalho em pé o dia todo", emoji: "🚶‍♀️" },
      { value: "1-2x", label: "Treino 1 a 2 vezes", emoji: "🏃‍♀️" },
      { value: "3x+", label: "Treino 3 vezes ou mais", emoji: "🔥" },
    ],
  },
  {
    kind: "pergunta",
    id: "peso",
    curto: "Peso desde que chegou",
    titulo: "O seu peso mudou desde que você chegou na Austrália?",
    opcoes: [
      { value: "ate5", label: "Engordei até 5 kg", emoji: "👖" },
      { value: "mais5", label: "Engordei mais de 5 kg", emoji: "🪞" },
      { value: "emagreci", label: "Emagreci", emoji: "📉" },
      { value: "igual", label: "Continua igual", emoji: "⚖️" },
      { value: "nao_sei", label: "Não sei, não me peso", emoji: "🤷‍♀️" },
    ],
  },
  {
    kind: "pergunta",
    id: "mudou",
    curto: "O que mudou na alimentação",
    titulo: "O que mais mudou na sua alimentação desde que você chegou?",
    opcoes: [
      { value: "pao_pronto", label: "Como mais pão e coisa pronta", emoji: "🥪" },
      { value: "pulo", label: "Pulo refeições", emoji: "⏭️" },
      { value: "fora", label: "Como fora demais", emoji: "🍔" },
      { value: "arroz_feijao", label: "Perdi o ritmo do arroz e feijão", emoji: "🍚" },
      { value: "quase_nada", label: "Quase nada mudou", emoji: "👌" },
    ],
  },
  {
    kind: "pergunta",
    id: "fim_dia",
    curto: "Fim do dia",
    titulo: "E no fim do dia, o que acontece?",
    opcoes: [
      { value: "aparece", label: "Como o que aparece", emoji: "🤷‍♀️" },
      { value: "delivery", label: "Peço delivery", emoji: "🛵" },
      { value: "belisco", label: "Belisco a noite inteira", emoji: "🍪" },
      { value: "tarde", label: "Janto de verdade, mas tarde", emoji: "🌙" },
      { value: "nem_janto", label: "Nem janto", emoji: "😶" },
      { value: "normal", label: "Janto normal, sem drama", emoji: "🍽️" },
    ],
  },
  {
    kind: "pergunta",
    id: "saude",
    curto: "Saúde",
    titulo: "Tem alguma coisa na sua saúde que você quer cuidar?",
    subtitulo: "Pode marcar mais de uma.",
    multi: true,
    opcoes: [
      { value: "intestino", label: "Intestino preso ou inchaço", emoji: "🫧" },
      { value: "exames", label: "Colesterol, glicose ou diabetes", emoji: "🩸" },
      { value: "hormonios", label: "Tireoide ou hormônios", emoji: "🦋" },
      { value: "ansiedade", label: "Ansiedade ou compulsão por comida", emoji: "🧠" },
      { value: "intolerancia", label: "Alergia ou intolerância (lactose, glúten)", emoji: "🌾" },
      { value: "gestacao", label: "Gestante ou amamentando", emoji: "🤰", soMulher: true },
      { value: "medicacao", label: "Uso medicação para emagrecer", emoji: "💊" },
      { value: "nenhuma", label: "Nada disso", emoji: "✅", exclusivo: true },
    ],
  },
  {
    kind: "pergunta",
    id: "cozinha",
    curto: "Cozinha",
    titulo: "Como é a sua rotina na cozinha?",
    opcoes: [
      { value: "dia", label: "Cozinho todo dia, coisa rápida", emoji: "🍳" },
      { value: "marmita", label: "Faço marmita uma vez na semana", emoji: "🥡" },
      { value: "quando_da", label: "Cozinho quando dá, sem rotina", emoji: "🎲" },
      { value: "nao_cozinho", label: "Quase não cozinho, compro pronto", emoji: "🛍️" },
      { value: "gosto", label: "Gosto de cozinhar e dou tempo pra isso", emoji: "👩‍🍳" },
    ],
  },

  // Bloco C: objetivo, o que impede e o que já tentou
  {
    kind: "pergunta",
    id: "objetivo",
    curto: "Objetivo (3 meses)",
    titulo: "O que você quer conquistar nos próximos 3 meses?",
    subtitulo: "Pode marcar mais de uma.",
    multi: true,
    opcoes: [
      { value: "emagrecer", label: "Emagrecer", emoji: "⚖️" },
      { value: "massa", label: "Ganhar massa", emoji: "💪" },
      { value: "energia", label: "Ter mais energia e disposição", emoji: "⚡" },
      { value: "saude", label: "Cuidar da saúde e melhorar exames", emoji: "🩺" },
      { value: "pazes", label: "Fazer as pazes com a comida", emoji: "🕊️" },
      { value: "comida_de_verdade", label: "Voltar a comer comida de verdade", emoji: "🍚" },
    ],
  },
  {
    kind: "pergunta",
    id: "impedimento",
    curto: "O que impede",
    titulo: "E o que mais te impede de chegar lá?",
    opcoes: [
      { value: "manter", label: "Sei o que fazer, mas não consigo manter", emoji: "🔁" },
      { value: "comprar", label: "Não sei o que comer com os produtos daqui", emoji: "🛒" },
      { value: "tempo", label: "Falta tempo e energia para cozinhar", emoji: "⏳" },
      { value: "emocional", label: "Como por ansiedade, estresse ou tédio", emoji: "😮‍💨" },
      { value: "sozinha", label: "Sinto que estou {sozinha} nisso", emoji: "🫂" },
    ],
  },
  {
    kind: "micro",
    id: "micro-2",
    eyebrow: "O que você me contou até aqui",
    imagem: "sistema",
    titulo: "",
    corpo: "",
    destaque: "No consultório, o que muda não é a força de vontade. É ter um plano que cabe na sua rotina e alguém olhando junto.",
    assinado: true,
    cta: "Continuar",
    dinamico: {
      perguntaId: "impedimento",
      textos: {
        manter: {
          titulo: "**Saber o que fazer não é o seu problema.** Manter é.",
          corpo: "{retrato} Manter não depende de força de vontade: depende de um plano que caiba na sua semana de verdade e de alguém ajustando quando a semana muda. {saude_frase}",
        },
        comprar: {
          titulo: "**Não saber o que comer aqui não é falta de informação.** É falta de um plano feito com os produtos daqui.",
          corpo: "{retrato} Rótulo em inglês, marca que você não conhece, comida de casa que não tem igual aqui: ninguém te deu um mapa. {saude_frase}",
        },
        tempo: {
          titulo: "**Não é falta de tempo.** É falta de um plano que caiba no tempo que você tem.",
          corpo: "{retrato} Plano que pede 40 minutos de fogão todo dia não serve para a sua semana. {saude_frase}",
        },
        emocional: {
          titulo: "**Comer por ansiedade não é falta de disciplina.** É o jeito que o corpo achou de aguentar a mudança.",
          corpo:
            "{retrato} Mudar de país mexe com tudo, e a comida costuma ser o primeiro lugar onde isso aparece. Cortar mais não resolve: o que resolve é entender quando e por que acontece. {saude_frase}",
        },
        sozinha: {
          titulo: "**Você não precisa resolver isso {sozinha}.**",
          corpo: "{retrato} Longe de casa e sem ter para quem perguntar, cada refeição vira mais uma decisão no seu dia. {saude_frase}",
        },
      },
    },
  },
  {
    kind: "pergunta",
    id: "acompanhamento",
    curto: "Nutricionista antes",
    titulo: "Você já fez acompanhamento com nutricionista?",
    opcoes: [
      { value: "nunca", label: "Nunca fiz", emoji: "🙋‍♀️" },
      { value: "brasil", label: "Já fiz no Brasil", emoji: "🇧🇷" },
      { value: "australia", label: "Já fiz aqui na Austrália", emoji: "🇦🇺" },
    ],
  },
  {
    kind: "pergunta",
    id: "experiencia",
    curto: "Como foi o acompanhamento anterior",
    titulo: "E como foi da última vez?",
    mostrarSe: (r) => typeof r.acompanhamento === "string" && r.acompanhamento !== "nunca",
    opcoes: [
      { value: "restritiva", label: "Dieta restritiva demais, não aguentei", emoji: "🥗" },
      { value: "rotina", label: "O plano não cabia na minha rotina", emoji: "⏰" },
      { value: "produtos", label: "Comida que eu não acho aqui", emoji: "🛒" },
      { value: "sozinha", label: "Faltou acompanhamento de perto", emoji: "📵" },
      { value: "parei", label: "Funcionou, mas parei e voltou tudo", emoji: "🔁" },
      { value: "bom", label: "Foi bom, quero retomar", emoji: "👍" },
    ],
  },
  {
    kind: "micro",
    id: "micro-3",
    eyebrow: "Como eu trabalho",
    imagem: "acompanhamento",
    titulo: "",
    corpo: "",
    destaque: "É isso que a gente desenha {juntas} na Sessão Clareza: 30 minutos, online e de graça.",
    assinado: true,
    cta: "Continuar",
    dinamico: {
      // Quem já fez acompanhamento ouve a resposta à objeção; quem nunca fez, o caminho para o objetivo.
      perguntaId: "experiencia",
      padrao: "nunca",
      textos: {
        nunca: {
          titulo: "Para {objetivos_frase}, **sem dieta de revista e sem plano que não cabe na sua vida**.",
          corpo: "{cozinha_contexto} {metodo}",
        },
        restritiva: {
          titulo: "**Da última vez, a dieta foi restritiva demais.** Desta vez o plano parte do que você já come.",
          corpo: "E você quer {objetivos_frase}. {cozinha_contexto} {metodo}",
        },
        rotina: {
          titulo: "**O plano anterior não cabia na sua rotina.** Desta vez ele nasce dela.",
          corpo: "E você quer {objetivos_frase}. {cozinha_contexto} {metodo}",
        },
        produtos: {
          titulo: "**A comida do plano anterior não existia aqui.** Desta vez tudo sai do mercado daqui.",
          corpo: "E você quer {objetivos_frase}. {cozinha_contexto} {metodo}",
        },
        sozinha: {
          titulo: "**Da última vez, faltou acompanhamento de perto.** Desta vez você fala comigo entre as consultas.",
          corpo: "E você quer {objetivos_frase}. {cozinha_contexto} {metodo}",
        },
        parei: {
          titulo: "**Funcionou, mas você parou.** Desta vez o plano é pensado para virar hábito.",
          corpo: "E você quer {objetivos_frase}. {cozinha_contexto} {metodo}",
        },
        bom: {
          titulo: "**Foi bom, e você quer retomar.** A gente parte do que funcionou para você.",
          corpo: "E você quer {objetivos_frase}. {cozinha_contexto} {metodo}",
        },
      },
    },
  },

  // Bloco D: desejo e compromisso
  {
    kind: "pergunta",
    id: "projecao",
    curto: "O que mais mudaria",
    titulo: "Imagina daqui a 3 meses, com isso resolvido. O que mudaria mais na sua vida?",
    opcoes: [
      { value: "energia", label: "Energia para o dia render", emoji: "⚡" },
      { value: "roupa", label: "A roupa cabendo de novo", emoji: "👕" },
      { value: "espelho", label: "Me sentir bem no espelho", emoji: "🪞" },
      { value: "paz", label: "Paz para comer sem culpa", emoji: "🕊️" },
      { value: "saude", label: "Exames e saúde em dia", emoji: "🩺" },
      { value: "tudo", label: "Tudo isso junto", emoji: "✨" },
    ],
  },
  {
    kind: "pergunta",
    id: "compromisso",
    curto: "Faria a sessão",
    titulo: "Se 30 minutos comigo te mostrassem por onde começar, você faria?",
    opcoes: [
      { value: "sim", label: "Sim, com certeza", emoji: "✅" },
      { value: "horario", label: "Sim, se tiver um horário que encaixe", emoji: "🗓️" },
      { value: "talvez", label: "Talvez, quero entender melhor", emoji: "🤔" },
      { value: "nao", label: "Agora não", emoji: "⏸️" },
    ],
  },

  // Bloco E: para preparar a conversa (qualificação)
  {
    kind: "pergunta",
    id: "urgencia",
    curto: "Urgência (0 a 10)",
    titulo: "De 0 a 10, quanto é urgente para você resolver isso agora?",
    subtitulo: "As últimas perguntas são para eu preparar a nossa conversa.",
    layout: "escala",
    extremos: ["Pode esperar", "Pra ontem"],
    opcoes: ESCALA_0_10,
  },
  {
    kind: "pergunta",
    id: "investimento",
    curto: "Investir no acompanhamento",
    titulo: "Se na nossa conversa fizer sentido um acompanhamento comigo, como você se sente sobre investir nisso agora?",
    opcoes: [
      { value: "pronta", label: "{Pronta} para investir, se fizer sentido", emoji: "💚" },
      { value: "organizar", label: "Quero, mas preciso me organizar antes", emoji: "🗓️" },
      { value: "gratuito", label: "Agora só consigo algo gratuito", emoji: "🙏" },
    ],
  },
  {
    kind: "pergunta",
    id: "renda",
    curto: "Renda por semana",
    titulo: "Mais ou menos, quanto você ganha por semana?",
    subtitulo: "Me ajuda a te indicar um caminho que caiba no seu momento. Fica só entre a gente.",
    opcoes: [
      { value: "ate800", label: "Até A$ 800", emoji: "🪙" },
      { value: "800-1200", label: "A$ 800 a 1.200", emoji: "💵" },
      { value: "1200-1800", label: "A$ 1.200 a 1.800", emoji: "💰" },
      { value: "1800+", label: "Mais de A$ 1.800", emoji: "💎" },
      { value: "nao_dizer", label: "Prefiro não dizer", emoji: "🤐" },
    ],
  },
  {
    kind: "pergunta",
    id: "decisao",
    curto: "Quem decide",
    titulo: "Quando o assunto é investir em você e na sua saúde, quem decide?",
    opcoes: [
      { value: "sozinha", label: "Eu decido {sozinha}", emoji: "🙋‍♀️" },
      { value: "conversa", label: "Converso com quem mora comigo antes", emoji: "💬" },
      { value: "depende", label: "Depende de outra pessoa", emoji: "🤝" },
    ],
  },
  {
    kind: "pergunta",
    id: "conheceu",
    curto: "Como conheceu",
    titulo: "Por onde você me conheceu?",
    opcoes: [
      { value: "instagram", label: "Instagram", emoji: "📸" },
      { value: "tiktok", label: "TikTok", emoji: "🎵" },
      { value: "google", label: "Google", emoji: "🔎" },
      { value: "indicacao", label: "Indicação de alguém", emoji: "💬" },
      { value: "outro", label: "Outro lugar", emoji: "✨" },
    ],
  },
];

export const TOTAL_TELAS = TELAS.length + 3; // + análise, espelho e captura

/** A tela aparece para estas respostas? (telas condicionais) */
export const telaVisivel = (tela: Tela, r: Respostas) => !tela.mostrarSe || tela.mostrarSe(r);

/** Próxima tela visível depois de `indice` (TELAS.length quando acabou). */
export const proximaTela = (indice: number, r: Respostas) => {
  let i = indice + 1;
  while (i < TELAS.length && !telaVisivel(TELAS[i], r)) i++;
  return i;
};

/** Tela visível anterior a `indice` (0 no mínimo). */
export const telaAnterior = (indice: number, r: Respostas) => {
  let i = indice - 1;
  while (i > 0 && !telaVisivel(TELAS[i], r)) i--;
  return Math.max(0, i);
};

const texto = (r: Respostas, id: string) => {
  const v = r[id];
  return Array.isArray(v) ? v[0] : v;
};

const lista = (r: Respostas, id: string) => {
  const v = r[id];
  return Array.isArray(v) ? v : v ? [v] : [];
};

const pergunta = (id: string) => TELAS.find((t): t is Pergunta => t.kind === "pergunta" && t.id === id);

const rotulo = (id: string, value: string | undefined) => pergunta(id)?.opcoes.find((o) => o.value === value)?.label;

/** Respostas de uma pergunta, só se ela apareceu para a pessoa (ignora resposta antiga de tela escondida). */
const respostasVisiveis = (r: Respostas, id: string) => {
  const p = pergunta(id);
  return p && telaVisivel(p, r) ? lista(r, id) : [];
};

/** Junta itens como em português: "a", "a e b", "a, b e c". */
const juntar = (itens: string[]) => (itens.length <= 1 ? itens.join("") : `${itens.slice(0, -1).join(", ")} e ${itens[itens.length - 1]}`);

const maiuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* ------------------------------------------------------------------ */
/* Perfil por pesos                                                    */
/* ------------------------------------------------------------------ */

/** Quanto cada resposta soma em cada eixo. O eixo com a maior porcentagem vira o perfil. */
const PESOS: Record<string, Record<string, Partial<Record<PerfilId, number>>>> = {
  impedimento: {
    comprar: { adaptacao: 5 },
    tempo: { rotina: 5 },
    emocional: { emocional: 5 },
    manter: { recomeco: 5 },
    sozinha: { recomeco: 4, emocional: 1 },
  },
  saude: {
    ansiedade: { emocional: 3 },
    gestacao: { saude: 8 },
    medicacao: { saude: 8 },
    exames: { saude: 5 },
    hormonios: { saude: 4 },
    intestino: { saude: 1 },
    intolerancia: { saude: 1 },
  },
  tempo_au: { "<6m": { adaptacao: 3 }, "6m-2a": { adaptacao: 1 } },
  rotina: { chegou: { adaptacao: 3 }, turnos: { rotina: 2 }, estudo: { rotina: 2 }, casa: { rotina: 1 }, escritorio: { rotina: 1 } },
  mudou: { pao_pronto: { rotina: 1, adaptacao: 1 }, pulo: { rotina: 2 }, fora: { rotina: 2 }, arroz_feijao: { adaptacao: 2 } },
  fim_dia: { aparece: { rotina: 1 }, delivery: { rotina: 2, emocional: 1 }, belisco: { emocional: 3 }, tarde: { rotina: 1 }, nem_janto: { rotina: 2 } },
  cozinha: { nao_cozinho: { rotina: 2 }, quando_da: { rotina: 1, recomeco: 1 } },
  experiencia: {
    parei: { recomeco: 3 },
    restritiva: { recomeco: 2, emocional: 1 },
    sozinha: { recomeco: 2 },
    rotina: { rotina: 2 },
    produtos: { adaptacao: 2 },
    bom: { recomeco: 1 },
  },
  objetivo: { saude: { saude: 2 }, pazes: { emocional: 2 }, comida_de_verdade: { adaptacao: 1 } },
  projecao: { paz: { emocional: 1 }, saude: { saude: 1 } },
};

/** Pontos de quem está claramente nesse eixo (vira 100%). Iguala eixos com mais e menos perguntas. */
const REFERENCIA: Record<PerfilId, number> = { adaptacao: 10, rotina: 9, emocional: 9, recomeco: 7, saude: 8 };

/** Desempate: o eixo do que a pessoa disse que impede, depois esta ordem. */
const ORDEM: PerfilId[] = ["saude", "emocional", "recomeco", "adaptacao", "rotina"];
const EIXO_DO_IMPEDIMENTO: Record<string, PerfilId> = { comprar: "adaptacao", tempo: "rotina", emocional: "emocional", manter: "recomeco", sozinha: "recomeco" };

export interface Eixo {
  id: PerfilId;
  rotulo: string;
  pontos: number;
  /** De 0 a 96, para as barras. */
  pct: number;
}

export const ROTULO_EIXO: Record<PerfilId, string> = {
  adaptacao: "Adaptação à comida daqui",
  rotina: "Rotina corrida",
  emocional: "Fome emocional",
  recomeco: "Ciclo de recomeços",
  saude: "Saúde",
};

/** Os 5 eixos com pontos e porcentagem, do mais forte para o mais fraco. */
export const pontuacao = (r: Respostas): Eixo[] => {
  const pontos: Record<PerfilId, number> = { adaptacao: 0, rotina: 0, emocional: 0, recomeco: 0, saude: 0 };
  for (const [id, tabela] of Object.entries(PESOS)) {
    for (const valor of respostasVisiveis(r, id)) {
      for (const [eixo, n] of Object.entries(tabela[valor] ?? {})) pontos[eixo as PerfilId] += n ?? 0;
    }
  }
  const primeiro = EIXO_DO_IMPEDIMENTO[texto(r, "impedimento") ?? ""];
  const prioridade = (id: PerfilId) => (id === primeiro ? -1 : ORDEM.indexOf(id));
  return (Object.keys(pontos) as PerfilId[])
    .map((id) => ({ id, rotulo: ROTULO_EIXO[id], pontos: pontos[id], pct: Math.min(96, Math.round((pontos[id] / REFERENCIA[id]) * 100)) }))
    .sort((a, b) => b.pct - a.pct || prioridade(a.id) - prioridade(b.id));
};

/** Perfil da pessoa. Gestação e medicação para emagrecer sempre caem em Saúde em Foco. */
export const perfilDe = (r: Respostas): PerfilId => {
  const saude = lista(r, "saude");
  if (saude.includes("gestacao") || saude.includes("medicacao")) return "saude";
  const [primeiro] = pontuacao(r);
  return primeiro.pontos > 0 ? primeiro.id : "rotina";
};

/** Eixos com algum ponto, para as barras (no máximo 4). */
export const eixosVisiveis = (r: Respostas) => {
  const perfil = perfilDe(r);
  const eixos = pontuacao(r).filter((e) => e.pontos > 0);
  // O perfil sempre aparece em primeiro (vale também para a regra de gestação e medicação).
  return [...eixos.filter((e) => e.id === perfil), ...eixos.filter((e) => e.id !== perfil)].slice(0, 4);
};

export interface Perfil {
  id: PerfilId;
  nome: string;
  /** Completa "Ana, …" no topo do resultado. */
  diagnostico: string;
  manchete: string;
  resumo: string;
  /** Por que a Sessão Clareza faz sentido para este perfil ({na_rotina} traz a rotina dela). */
  porqueSessao: string;
  /** Primeiro item de "O que a gente vê em 30 minutos". */
  itemSessao: string;
}

export const PERFIS: Record<PerfilId, Perfil> = {
  adaptacao: {
    id: "adaptacao",
    nome: "Adaptação",
    diagnostico: "o que te trava hoje é a adaptação à comida daqui.",
    manchete: "A comida da Austrália ainda não virou a sua comida.",
    resumo:
      "Quando o mercado, os produtos e os rótulos são outros, a comida costuma virar o que é mais fácil, e não o que faz bem. Não é falta de informação nem de vontade: falta um mapa feito com o que existe aqui.",
    porqueSessao:
      "Na Sessão Clareza eu entendo como você come hoje, {na_rotina}, o que funcionava antes de você vir, e te mostro por onde começar com o que tem no Coles, Woolworths e Aldi.",
    itemSessao: "O que comer aqui com o que tem no Coles, Woolworths e Aldi",
  },
  rotina: {
    id: "rotina",
    nome: "Rotina Corrida",
    diagnostico: "o que te trava hoje é a rotina, não a força de vontade.",
    manchete: "A sua comida costuma ficar para depois, porque o dia não deixa espaço para ela.",
    resumo:
      "Quando o dia é corrido, comer vira o que dá, na hora que dá. Não é falta de disciplina: é que nenhum plano foi feito para o tempo que você realmente tem.",
    porqueSessao: "Plano pronto parte de um dia que não é o seu. Na Sessão Clareza eu olho como é a sua semana, {na_rotina}, e te mostro onde a comida se encaixa nela.",
    itemSessao: "Como a comida cabe no tempo que você tem hoje",
  },
  emocional: {
    id: "emocional",
    nome: "Fome Emocional",
    diagnostico: "o que te trava hoje é a fome emocional, não a falta de disciplina.",
    manchete: "Quando o dia pesa, é a comida que costuma segurar a barra.",
    resumo:
      "Mudar de país mexe com tudo, e a comida costuma ser o primeiro lugar onde isso aparece. Cortar mais e se cobrar mais não resolve. O que resolve é entender quando e por que a fome emocional aparece, e cuidar disso com calma.",
    porqueSessao:
      "Fome emocional não se resolve com cardápio mais rígido. Na Sessão Clareza a gente conversa, sem julgamento, sobre quando ela aparece, {na_rotina}, e qual é o primeiro passo para ela perder força.",
    itemSessao: "Quando e por que a fome emocional aparece no seu dia",
  },
  recomeco: {
    id: "recomeco",
    nome: "Recomeço",
    diagnostico: "o que te trava hoje é o ciclo de começar e parar.",
    manchete: "Você sabe o que fazer. O difícil é continuar.",
    resumo:
      "Começa com tudo, a semana aperta, para, e depois recomeça. Esse ciclo costuma acontecer quando o plano não acompanha a vida real e não tem ninguém ajustando junto. Não é falta de força de vontade.",
    porqueSessao:
      "O que quebra esse ciclo não é mais uma dieta: é acompanhamento, alguém ajustando o plano quando a semana muda. Na Sessão Clareza eu te mostro como isso funcionaria, {na_rotina}.",
    itemSessao: "Por que você para, e o que muda desta vez",
  },
  saude: {
    id: "saude",
    nome: "Saúde em Foco",
    diagnostico: "o seu caso pede um plano feito para a sua saúde.",
    manchete: "A sua alimentação precisa olhar para a sua saúde, não só para a balança.",
    resumo:
      "Quando existe uma condição de saúde, uma fase especial ou uma medicação, dieta da internet não serve. O plano precisa ser individual, feito com cuidado e olhando os seus exames.",
    porqueSessao:
      "Antes de qualquer orientação, eu preciso entender o seu caso. Na Sessão Clareza eu vejo o que você marcou, como você come, {na_rotina}, e, se tiver, os seus exames recentes.",
    itemSessao: "O que a sua saúde pede do seu plano",
  },
};

export const PERFIS_IDS = Object.keys(PERFIS) as PerfilId[];

/** Ilustração do perfil ("Hoje"), na versão do gênero da pessoa. */
export const imagemDoPerfil = (perfil: PerfilId, sexo: string | string[] | undefined): ImagemQuiz =>
  sexo === "homem" ? `perfil-${perfil}-homem` : `perfil-${perfil}`;

/** Marcações de saúde que pedem um olhar individual (aparecem no resultado e no e-mail da Clara). */
const FLAGS_SAUDE = ["gestacao", "medicacao", "exames", "hormonios", "ansiedade"];

export const flagsSaude = (r: Respostas) => lista(r, "saude").filter((v) => FLAGS_SAUDE.includes(v));

export const temFlagSaude = (r: Respostas) => flagsSaude(r).length > 0;

/* ------------------------------------------------------------------ */
/* Frases e tokens                                                     */
/* ------------------------------------------------------------------ */

/** Pedaços do retrato ("Você trabalha em turnos, está na Austrália…"), um por resposta. */
const RETRATO = {
  rotina: {
    turnos: "trabalha em turnos",
    estudo: "divide o dia entre estudo e trabalho",
    casa: "cuida da casa e dos filhos",
    escritorio: "trabalha em horário de escritório",
    chegou: "acabou de chegar",
  } as Record<string, string>,
  tempo_au: {
    "<6m": "está na Austrália há menos de 6 meses",
    "6m-2a": "está na Austrália entre 6 meses e 2 anos",
    "2-5a": "está na Austrália de 2 a 5 anos",
    "5a+": "está na Austrália há mais de 5 anos",
  } as Record<string, string>,
  /** Completado com "desde então" (quando a frase já fala da chegada) ou "desde que chegou". */
  peso: {
    mais5: "engordou mais de 5 kg",
    ate5: "engordou até 5 kg",
    emagreci: "emagreceu",
  } as Record<string, string>,
  mudou: {
    pao_pronto: "passou a comer mais pão e coisa pronta",
    pulo: "pula refeições",
    fora: "come fora mais do que gostaria",
    arroz_feijao: "perdeu o ritmo do arroz e feijão",
  } as Record<string, string>,
  /** [quando, o quê]: o "quando" ganha vírgulas no meio da frase. */
  fim_dia: {
    aparece: ["no fim do dia", "come o que aparece"],
    delivery: ["no fim do dia", "pede delivery"],
    belisco: ["à noite", "belisca até a hora de dormir"],
    tarde: ["", "janta de verdade, mas tarde"],
    nem_janto: ["", "muitas vezes nem janta"],
  } as Record<string, [string, string]>,
};

/** O retrato dela em duas frases, só com o que ela respondeu. */
export const retrato = (r: Respostas) => {
  const rotina = texto(r, "rotina");
  const tempo = texto(r, "tempo_au");
  const primeira = [RETRATO.rotina[rotina ?? ""], rotina === "chegou" && tempo === "<6m" ? undefined : RETRATO.tempo_au[tempo ?? ""]].filter((p): p is string => Boolean(p));
  const peso = RETRATO.peso[texto(r, "peso") ?? ""];
  if (peso) primeira.push(`${peso} ${primeira.length ? "desde então" : "desde que chegou"}`);
  const mudou = RETRATO.mudou[texto(r, "mudou") ?? ""];
  const fim = RETRATO.fim_dia[texto(r, "fim_dia") ?? ""];
  let segunda = "";
  if (mudou && fim) segunda = fim[0] ? `${mudou} e, ${fim[0]}, ${fim[1]}` : `${mudou} e ${fim[1]}`;
  else if (mudou) segunda = mudou;
  else if (fim) segunda = fim[0] ? `${fim[0]}, ${fim[1]}` : fim[1];
  return [primeira.length ? `Você ${juntar(primeira)}.` : "", segunda ? `${maiuscula(segunda)}.` : ""].filter(Boolean).join(" ");
};

/** Uma frase sobre a saúde, quando ela marcou algo que muda o plano. */
const saudeFrase = (r: Respostas) => {
  const saude = lista(r, "saude");
  const emocional = texto(r, "impedimento") === "emocional";
  if (saude.includes("gestacao")) return "E, na gestação ou amamentação, o plano precisa ser ainda mais individual.";
  if (saude.includes("medicacao")) return "E, com medicação para emagrecer, a alimentação pede atenção especial.";
  if (saude.includes("ansiedade")) return emocional ? "E você marcou ansiedade também: isso pede cuidado, não cobrança." : "E você marcou ansiedade: ela costuma aparecer no prato também.";
  if (saude.includes("exames")) return "E você quer cuidar de colesterol, glicose ou diabetes: o plano precisa olhar para os seus exames.";
  if (saude.includes("hormonios")) return "E com tireoide ou hormônios na conta, o plano precisa ser individual.";
  if (saude.includes("intestino")) return "E o intestino entra na conta desde o começo.";
  return "";
};

const OBJETIVO_FRASE: Record<string, string> = {
  emagrecer: "emagrecer",
  massa: "ganhar massa",
  energia: "ter mais energia",
  saude: "cuidar da saúde",
  pazes: "fazer as pazes com a comida",
  comida_de_verdade: "voltar a comer comida de verdade",
};

const objetivosFrase = (r: Respostas) => juntar(lista(r, "objetivo").map((v) => OBJETIVO_FRASE[v]).filter(Boolean)) || "comer melhor";

const NA_ROTINA: Record<string, string> = {
  turnos: "na sua rotina de turnos",
  estudo: "entre o estudo e o trabalho",
  casa: "entre a casa e os filhos",
  escritorio: "no seu horário de escritório",
  chegou: "nesses primeiros meses aqui",
  outro: "no seu dia a dia",
};

const FRASES: Record<string, Record<string, string>> = {
  cozinha_contexto: {
    dia: "Como você cozinha todo dia, o seu plano usa receitas rápidas, de 10 a 20 minutos.",
    marmita: "Como você faz marmita, o seu plano já sai organizado para o dia da marmita.",
    quando_da: "Como você cozinha quando dá, o seu plano tem a opção de 10 minutos para os dias corridos.",
    nao_cozinho: "Como você quase não cozinha, a gente começa pelo básico, sem fogão longo.",
    gosto: "Como você gosta de cozinhar, o seu plano tem receita de verdade e liberdade para trocar.",
  },
  idade_frase: {
    "18-24": "Nessa idade, a rotina muda rápido, e a comida costuma ser a primeira a ficar para trás.",
    "25-34": "Entre os 25 e os 34, trabalho e vida aqui costumam ocupar tudo, e a comida vai junto.",
    "35-44": "Depois dos 35, o corpo costuma sentir mais rápido o que muda no prato.",
    "45+": "Depois dos 45, o corpo costuma pedir mais cuidado com o que vai no prato.",
  },
  tempo_analise: {
    "<6m": "nos seus primeiros meses aqui",
    "6m-2a": "desde que você chegou",
    "2-5a": "desde que você chegou",
    "5a+": "nos seus anos de Austrália",
  },
  rotina_analise: {
    turnos: "de turnos",
    estudo: "de estudo e trabalho",
    casa: "de casa e filhos",
    escritorio: "de escritório",
    chegou: "de quem acabou de chegar",
  },
  impedimento_analise: {
    manter: "Entendendo por que é difícil manter",
    comprar: "Mapeando o que você come com os produtos daqui",
    tempo: "Medindo quanto tempo sobra para a comida",
    emocional: "Entendendo quando a fome emocional aparece",
    sozinha: "Vendo onde está faltando apoio",
  },
};

const frase = (grupo: string, r: Respostas, id: string) => FRASES[grupo][texto(r, id) ?? ""] ?? "";

/** Tokens disponíveis nos textos (ver uso nas TELAS e nos PERFIS). */
const TOKENS: Record<string, (r: Respostas) => string> = {
  cidade_frase: (r) => {
    const c = texto(r, "cidade");
    return c && c !== "Outra" ? `Em ${c}` : "Na Austrália";
  },
  brasileiras: (r) => (texto(r, "sexo") === "homem" ? "brasileiros" : "brasileiras"),
  sozinha: (r) => (texto(r, "sexo") === "homem" ? "sozinho" : "sozinha"),
  juntas: (r) => (texto(r, "sexo") === "homem" ? "juntos" : "juntas"),
  pronta: (r) => (texto(r, "sexo") === "homem" ? "pronto" : "pronta"),
  Pronta: (r) => (texto(r, "sexo") === "homem" ? "Pronto" : "Pronta"),
  idade_frase: (r) => frase("idade_frase", r, "idade"),
  retrato: (r) => retrato(r),
  saude_frase: (r) => saudeFrase(r),
  objetivos_frase: (r) => objetivosFrase(r),
  na_rotina: (r) => NA_ROTINA[texto(r, "rotina") ?? ""] ?? NA_ROTINA.outro,
  cozinha_contexto: (r) => frase("cozinha_contexto", r, "cozinha"),
  metodo: () =>
    "Tudo sai do que você encontra no Coles, Woolworths e Aldi, em cima dos seus horários. Entre as consultas, você fala comigo direto no WhatsApp, e o plano muda quando a sua semana muda.",
  rotina_analise: (r) => frase("rotina_analise", r, "rotina"),
  tempo_analise: (r) => frase("tempo_analise", r, "tempo_au") || "desde que você chegou",
  impedimento_analise: (r) => frase("impedimento_analise", r, "impedimento") || "Entendendo o que te trava",
};

/** Troca os {tokens} pelas respostas da pessoa. */
export const preencher = (t: string, r: Respostas) =>
  t
    .replace(/\{(\w+)\}/g, (_, k: string) => (TOKENS[k] ? TOKENS[k](r) : `{${k}}`))
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,])/g, "$1")
    .trim();

/** Resolve o texto final de um micro-conteúdo (dinâmico + tokens). */
export const textoMicro = (tela: Micro, r: Respostas): MicroTexto => {
  let base: MicroTexto = { titulo: tela.titulo, corpo: tela.corpo, destaque: tela.destaque };
  const d = tela.dinamico;
  if (d) {
    const chave = respostasVisiveis(r, d.perguntaId)[0];
    const escolhido = (chave && d.textos[chave]) || (d.padrao && d.textos[d.padrao]) || Object.values(d.textos)[0];
    base = { ...escolhido, destaque: escolhido.destaque ?? tela.destaque };
  }
  return {
    titulo: preencher(base.titulo, r),
    corpo: preencher(base.corpo, r),
    destaque: base.destaque ? preencher(base.destaque, r) : undefined,
  };
};

/* ------------------------------------------------------------------ */
/* Espelho e resultado                                                 */
/* ------------------------------------------------------------------ */

export interface CardEspelho {
  /** O que a pessoa disse, com `**realce**`. */
  disse: string;
  resposta: string;
  /** dor = realce vermelho; bom = realce verde. */
  tom: "dor" | "bom";
}

const CARDS_PESO: Record<string, CardEspelho> = {
  mais5: { tom: "dor", disse: "Você **engordou mais de 5 kg** desde que chegou", resposta: "É muito comum nos primeiros anos aqui, e não é falta de disciplina." },
  ate5: { tom: "dor", disse: "Você **engordou até 5 kg** desde que chegou", resposta: "Quanto antes a gente ajusta, mais fácil é voltar." },
  emagreci: { tom: "dor", disse: "Você **emagreceu** desde que chegou", resposta: "Vale olhar se foi com saúde ou pulando refeição." },
};

const CARDS_IMPEDIMENTO: Record<string, CardEspelho> = {
  manter: { tom: "dor", disse: "Você **sabe o que fazer, mas não consegue manter**", resposta: "Por isso o acompanhamento: ajuste a cada consulta e WhatsApp direto comigo entre elas." },
  comprar: { tom: "dor", disse: "Você **não sabe o que comer** com os produtos daqui", resposta: "O plano já vem com o que tem no Coles, Woolworths e Aldi." },
  tempo: { tom: "dor", disse: "Falta **tempo e energia** para cozinhar", resposta: "O plano cabe no tempo que você tem, não no que sobraria num dia perfeito." },
  emocional: { tom: "dor", disse: "Você **come por ansiedade**, estresse ou tédio", resposta: "Isso se trabalha olhando o comportamento, não só o cardápio." },
  sozinha: { tom: "dor", disse: "Você se sente **{sozinha} nisso**", resposta: "Entre as consultas, você fala comigo direto no WhatsApp." },
};

const CARDS_EXPERIENCIA: Record<string, CardEspelho> = {
  restritiva: { tom: "dor", disse: "Da última vez, a **dieta foi restritiva demais**", resposta: "Aqui o plano parte do que você já come, com comida brasileira." },
  rotina: { tom: "dor", disse: "O plano anterior **não cabia na sua rotina**", resposta: "O seu plano é montado em cima dos seus horários reais." },
  produtos: { tom: "dor", disse: "Você **não achava a comida** do plano aqui", resposta: "Tudo com produtos do Coles, Woolworths e Aldi." },
  sozinha: { tom: "dor", disse: "Da última vez, **faltou acompanhamento** de perto", resposta: "WhatsApp direto comigo e ajuste sem esperar a próxima consulta." },
  parei: { tom: "dor", disse: "Funcionou, mas **você parou e voltou tudo**", resposta: "O acompanhamento costuma ter no mínimo 3 meses: o tempo de virar hábito." },
  bom: { tom: "bom", disse: "Você **quer retomar** o acompanhamento", resposta: "A gente parte do que já funcionou para você." },
};

const CARDS_SAUDE: Record<string, CardEspelho> = {
  gestacao: { tom: "bom", disse: "Você está **gestante ou amamentando**", resposta: "Fase que pede um plano individual, feito com cuidado." },
  medicacao: { tom: "bom", disse: "Você **usa medicação para emagrecer**", resposta: "Durante o tratamento a alimentação pede atenção especial, e isso a gente olha junto." },
  exames: { tom: "bom", disse: "Você quer cuidar de **colesterol, glicose ou diabetes**", resposta: "Eu analiso os seus exames e acompanho a evolução." },
  hormonios: { tom: "bom", disse: "Você quer cuidar da **tireoide ou dos hormônios**", resposta: "O plano leva isso em conta desde o começo, junto com os seus exames." },
  ansiedade: { tom: "bom", disse: "Você convive com **ansiedade ou compulsão** por comida", resposta: "Isso entra na conversa desde o começo, com cuidado e sem julgamento." },
  intestino: { tom: "bom", disse: "Você tem **intestino preso ou inchaço**", resposta: "Intestino e inchaço entram no plano desde o começo." },
};

const CARDS_MUDOU: Record<string, CardEspelho> = {
  pao_pronto: { tom: "dor", disse: "Você **come mais pão e coisa pronta**", resposta: "O plano troca o pronto por opções tão práticas quanto, com os produtos daqui." },
  pulo: { tom: "dor", disse: "Você **pula refeições**", resposta: "Pular refeição cobra a conta à noite. O plano começa por aí." },
  fora: { tom: "dor", disse: "Você **come fora demais**", resposta: "Comer fora entra no plano, em vez de ser o que desanda a semana." },
  arroz_feijao: { tom: "dor", disse: "Você **perdeu o ritmo do arroz e feijão**", resposta: "Ele volta, com o que tem no mercado daqui." },
};

const CARDS_FIM_DIA: Record<string, CardEspelho> = {
  aparece: { tom: "dor", disse: "No fim do dia você **come o que aparece**", resposta: "Uma opção de 10 minutos no plano existe exatamente para esse momento." },
  delivery: { tom: "dor", disse: "No fim do dia você **pede delivery**", resposta: "Comida pronta na geladeira é o que impede o pedido." },
  belisco: { tom: "dor", disse: "Você **belisca a noite inteira**", resposta: "Beliscar à noite costuma ser fome chegando tarde. Com o lanche planejado, a noite muda." },
  tarde: { tom: "dor", disse: "Você **janta de verdade, mas tarde**", resposta: "Jantar tarde não é problema. Jantar tarde sem plano é." },
  nem_janto: { tom: "dor", disse: "Muitas vezes você **nem janta**", resposta: "Pular o jantar derruba a energia do dia seguinte." },
};

/** Cards "você me contou que…", na ordem do que mais pesa para a Sessão Clareza (até 6). */
export const espelho = (r: Respostas): CardEspelho[] => {
  const cards: (CardEspelho | undefined)[] = [
    CARDS_PESO[texto(r, "peso") ?? ""],
    CARDS_IMPEDIMENTO[texto(r, "impedimento") ?? ""],
    CARDS_EXPERIENCIA[respostasVisiveis(r, "experiencia")[0] ?? ""],
    ...lista(r, "saude")
      .filter((v) => !(v === "ansiedade" && texto(r, "impedimento") === "emocional"))
      .slice(0, 2)
      .map((v) => CARDS_SAUDE[v]),
    CARDS_MUDOU[texto(r, "mudou") ?? ""],
    CARDS_FIM_DIA[texto(r, "fim_dia") ?? ""],
  ];
  return cards
    .filter((c): c is CardEspelho => Boolean(c))
    .slice(0, 6)
    .map((c) => ({ ...c, disse: preencher(c.disse, r), resposta: preencher(c.resposta, r) }));
};

/** Frase do espelho em uma linha, com os dados dela. */
export const fraseEspelho = (r: Respostas) => {
  const partes: string[] = [];
  const sexo = texto(r, "sexo");
  const idade = rotulo("idade", texto(r, "idade"));
  if (sexo && idade) partes.push(`${sexo === "homem" ? "Homem" : "Mulher"}, ${idade.toLowerCase()}`);
  const cidade = texto(r, "cidade");
  if (cidade && cidade !== "Outra") partes.push(`mora em ${cidade}`);
  const tempo = RETRATO.tempo_au[texto(r, "tempo_au") ?? ""];
  if (tempo) partes.push(tempo);
  const rotina = rotulo("rotina", texto(r, "rotina"));
  if (rotina) partes.push(rotina.toLowerCase());
  const objetivos = lista(r, "objetivo").map((v) => OBJETIVO_FRASE[v]).filter(Boolean);
  if (objetivos.length) partes.push(`quer ${juntar(objetivos)}`);
  if (partes.length === 0) return "";
  return maiuscula(preencher(partes.join(", "), r)) + ".";
};

/** "Pelo que você me contou": o retrato, a saúde e a resposta à experiência anterior. */
const MOTIVO_EXPERIENCIA: Record<string, string> = {
  restritiva: "Da última vez a dieta foi restritiva demais. Aqui o plano parte do que você já come.",
  rotina: "Da última vez o plano não cabia na sua rotina. Aqui ele nasce dela.",
  produtos: "Da última vez a comida do plano não existia aqui. Aqui tudo sai do mercado daqui.",
  sozinha: "Da última vez faltou acompanhamento de perto. Aqui você fala comigo entre as consultas.",
  parei: "Da última vez funcionou, mas você parou. Aqui o plano é pensado para virar hábito.",
  bom: "Da última vez foi bom. A gente retoma do que funcionou para você.",
};

export const motivosSessao = (r: Respostas) =>
  [retrato(r), saudeFrase(r), MOTIVO_EXPERIENCIA[respostasVisiveis(r, "experiencia")[0] ?? ""]].filter((m): m is string => Boolean(m)).map((m) => preencher(m, r));

/** O que ela quer daqui a 3 meses (resposta da projeção), para o cartão do resultado. */
const DESEJO: Record<string, string> = {
  energia: "Energia para o dia render",
  roupa: "A roupa cabendo de novo",
  espelho: "Se sentir bem no espelho",
  paz: "Paz para comer sem culpa",
  saude: "Exames e saúde em dia",
  tudo: "Energia, saúde e paz com a comida, tudo junto",
};

export const desejo = (r: Respostas) => DESEJO[texto(r, "projecao") ?? ""];

/** Itens de "O que a gente vê em 30 minutos", montados pelas respostas (até 5, mais o fechamento). */
const ITEM_ROTINA: Record<string, string> = {
  turnos: "Onde encaixar as refeições nos dias de turno e nos de folga",
  estudo: "Como comer bem nos dias de aula e trabalho",
  casa: "Como a mesma comida pode servir para você e para a família",
  escritorio: "O que levar para o trabalho e como segurar a fome da tarde",
  chegou: "O que substitui aqui a comida que você comia no Brasil",
};
const ITEM_FIM_DIA: Record<string, string> = {
  delivery: "O que está por trás do delivery no fim do dia",
  belisco: "Por que a fome aparece à noite, e como segurar",
  aparece: "Uma saída de 10 minutos para quando a fome chega e não tem nada pronto",
  nem_janto: "Como não chegar ao fim do dia sem comer",
  tarde: "Como fica o jantar tarde dentro do plano",
};
const ITEM_EXPERIENCIA: Record<string, string> = {
  restritiva: "Um plano que não seja restritivo como da última vez",
  parei: "O que fazer diferente para não parar de novo",
  sozinha: "Como funciona o apoio entre as consultas",
  rotina: "Um plano que caiba nos seus horários de verdade",
  produtos: "Como trocar a comida do plano antigo pelo que tem aqui",
  bom: "O que manter do que funcionou da última vez",
};
const ITEM_SAUDE: Record<string, string> = {
  gestacao: "O que muda na alimentação com a gestação ou a amamentação",
  medicacao: "Como comer bem durante o tratamento com a medicação",
  exames: "O que os seus exames pedem do seu prato",
  hormonios: "Como a tireoide ou os hormônios entram no plano",
  ansiedade: "Como a ansiedade aparece no seu prato",
};

export const itensSessao = (r: Respostas) => {
  const perfil = PERFIS[perfilDe(r)];
  const flag = flagsSaude(r).find((v) => !(v === "ansiedade" && perfil.id === "emocional"));
  const itens = [
    perfil.itemSessao,
    lista(r, "objetivo").length ? `O primeiro passo para ${objetivosFrase(r)}` : undefined,
    ITEM_FIM_DIA[texto(r, "fim_dia") ?? ""],
    ITEM_EXPERIENCIA[respostasVisiveis(r, "experiencia")[0] ?? ""],
    flag && ITEM_SAUDE[flag],
    ITEM_ROTINA[texto(r, "rotina") ?? ""],
  ].filter((i): i is string => Boolean(i));
  return [...new Set(itens)].slice(0, 5);
};

/** Frase do cartão de agendamento, pelo compromisso (ou pela urgência). */
export const fraseAgendar = (r: Respostas) => {
  switch (texto(r, "compromisso")) {
    case "sim":
      return "Você disse que faria a sessão. Então escolha o seu horário agora.";
    case "horario":
      return "Você disse que faria, se o horário encaixasse. Os horários são flexíveis, inclusive para quem trabalha em turnos.";
    case "talvez":
      return "Você disse que quer entender melhor. É exatamente para isso que a sessão existe, sem compromisso.";
  }
  const urgencia = Number(texto(r, "urgencia"));
  return urgencia >= 7 ? `Você deu nota ${urgencia} para a urgência. Então não deixa para depois: escolha o seu horário agora.` : "";
};

/** Rótulos das marcações de saúde que pedem olhar individual (para o bloco do resultado).
 *  No perfil Fome Emocional a ansiedade já é o assunto principal, então não se repete aqui. */
export const rotulosFlagsSaude = (r: Respostas) => {
  const emocional = perfilDe(r) === "emocional";
  return flagsSaude(r)
    .filter((v) => !(emocional && v === "ansiedade"))
    .map((v) => rotulo("saude", v)!.toLowerCase());
};

/** Perguntas que todo mundo faz sobre a Sessão Clareza (fatos das páginas da consulta e do acompanhamento). */
export const FAQ_CLAREZA = [
  {
    question: "A Sessão Clareza é gratuita mesmo?",
    answer: "É. Sem cartão, sem contrato e sem obrigação de continuar. São cerca de 30 minutos para eu entender o seu caso e te dizer o melhor caminho.",
  },
  {
    question: "Como funciona?",
    answer: "Por videochamada, no horário que você escolher na minha agenda. Atendo em horários flexíveis, inclusive para quem trabalha em turnos. Se você mora em Brisbane, também dá para ser presencial no consultório em Hamilton.",
  },
  {
    question: "Preciso me preparar?",
    answer: "Não. Eu já vou ter lido as suas respostas deste diagnóstico. Se tiver exames recentes, pode separar que eu dou uma olhada.",
  },
  {
    question: "Vou sair da sessão com um plano alimentar?",
    answer: "A sessão é uma avaliação: você sai sabendo os primeiros passos. O plano alimentar personalizado faz parte do acompanhamento, e é nessa conversa que eu explico as opções, o investimento e o que você recebe.",
  },
  {
    question: "E se eu não quiser continuar?",
    answer: "Sem problema nenhum. Você decide sem pressão, e a conversa já vai ter valido a pena.",
  },
];

/** Como é o acompanhamento, se fizer sentido depois da sessão (fatos da página de acompanhamento). */
export const ACOMPANHAMENTO = [
  "Plano feito com os produtos do Coles, Woolworths e Aldi",
  "Montado em cima dos seus horários, gostos e orçamento",
  "Consultas regulares e ajuste sempre que a rotina mudar",
  "WhatsApp direto comigo entre as consultas",
];

/* ------------------------------------------------------------------ */
/* O que chega para a Clara                                            */
/* ------------------------------------------------------------------ */

/** Valor de uma resposta numa tabela de pontos (padrão quando a resposta não está na tabela). */
const pontosDe = (tabela: Record<string, number>, resposta: string | undefined, padrao = 0) =>
  resposta !== undefined && resposta in tabela ? tabela[resposta] : padrao;

/** Temperatura do lead (0 a 10) pela urgência, compromisso, disposição para investir, renda e quem decide. */
export const temperaturaLead = (r: Respostas) => {
  const urgencia = Number(texto(r, "urgencia") ?? 0);
  const compromisso = pontosDe({ sim: 1, horario: 0.75, talvez: 0.25, nao: 0 }, texto(r, "compromisso"));
  const investimento = pontosDe({ pronta: 3, organizar: 1.5, gratuito: 0 }, texto(r, "investimento"));
  const decisao = pontosDe({ sozinha: 1, conversa: 0.5, depende: 0 }, texto(r, "decisao"));
  const renda = pontosDe({ ate800: 0, "800-1200": 0.5, "1200-1800": 1, "1800+": 1, nao_dizer: 0.5 }, texto(r, "renda"));
  const pontos = Math.round(((urgencia * 0.5 + compromisso + investimento + decisao + renda) / 11) * 100) / 10;
  const nivel = pontos >= 7 ? "quente" : pontos >= 4 ? "morna" : "fria";
  const rotuloNivel = { quente: "🔥 Quente", morna: "🌤️ Morna", fria: "❄️ Fria" }[nivel];
  return { pontos, nivel, rotulo: `${rotuloNivel} (${pontos}/10)` } as const;
};

const respostaLegivel = (p: Pergunta, r: Respostas) => {
  const valores = lista(r, p.id);
  if (valores.length === 0) return "";
  if (p.layout === "escala") return valores[0];
  return valores.map((v) => preencher(p.opcoes.find((o) => o.value === v)?.label ?? v, r)).join(", ");
};

/** Pergunta curta → resposta legível, só das perguntas que a pessoa viu e respondeu. */
export const resumoRespostas = (r: Respostas) =>
  TELAS.filter((t): t is Pergunta => t.kind === "pergunta" && telaVisivel(t, r))
    .map((p) => ({ id: p.id, pergunta: p.curto, resposta: respostaLegivel(p, r) }))
    .filter((l) => l.resposta);

/** Campos do lead para a função `submit-questionnaire` (e-mail da Clara e painel /admin). */
export const dadosDoLead = (r: Respostas) => {
  const resumo = resumoRespostas(r);
  const de = (id: string) => resumo.find((l) => l.id === id)?.resposta ?? "";
  return {
    perfil: PERFIS[perfilDe(r)].nome,
    eixos: eixosVisiveis(r)
      .map((e) => `${e.rotulo} ${e.pct}%`)
      .join(" · "),
    temperatura: temperaturaLead(r).rotulo,
    compromisso: de("compromisso"),
    urgencia: de("urgencia"),
    investimento: de("investimento"),
    renda: de("renda"),
    decisao: de("decisao"),
    rotina: de("rotina"),
    objetivo: de("objetivo"),
    impedimento: de("impedimento"),
    acompanhamento_anterior: [de("acompanhamento"), de("experiencia")].filter(Boolean).join(" · "),
    saude: de("saude"),
    flag_saude: temFlagSaude(r) ? "sim" : "não",
    cidade: de("cidade"),
    conheceu: de("conheceu"),
    resumo: resumo.map((l) => `${l.pergunta}: ${l.resposta}`).join("\n"),
    /** Legível também na versão antiga da função (que lista objetos como "chave: valor"). */
    respostas: Object.fromEntries(resumo.map((l) => [l.pergunta, l.resposta])),
  };
};

/** Texto que já vem no campo de observações da agenda (a pessoa vê e pode editar; a Clara recebe no agendamento). */
export const notaAgenda = (r: Respostas) => {
  const resumo = resumoRespostas(r);
  const de = (id: string) => resumo.find((l) => l.id === id)?.resposta;
  return [
    `Fiz o diagnóstico: perfil ${PERFIS[perfilDe(r)].nome}.`,
    de("objetivo") && `Quero: ${de("objetivo")!.toLowerCase()}.`,
    de("impedimento") && `O que mais me impede: ${de("impedimento")!.toLowerCase()}.`,
    de("urgencia") && `Urgência: ${de("urgencia")}/10.`,
  ]
    .filter(Boolean)
    .join(" ");
};

/** Etapas da tela de análise, com as respostas dela. */
export const ETAPAS_ANALISE = ["Lendo a sua rotina {rotina_analise}", "Cruzando com o que mudou {tempo_analise}", "{impedimento_analise}", "Montando o seu perfil"];

export const STORAGE_KEY = "diagnostico_clareza";
